<template>
  <div
    v-if="bookingReady"
    class="booking-check-out"
  >
    <div class="booking-checkout-form-wrapper">
      <modify-edit-banner
        v-if="isBookingEditMode"
        :is-room-list="false"
        :is-guest-or-payment-info="true"
      />
      <checkout-form
        :key="JSON.stringify(user)"
        ref="checkoutForm"
        :redirect-url="checkoutRedirectUrl"
        @loading="isLoading = $event"
        @error="onExternalError($event)"
        @external-error="onExternalError($event)"
      >
        <form
          slot-scope="{ serverErrorMsg }"
          class="mohg-form booking-check-out-form"
          :action="checkoutFormApi"
        >
          <fieldset>
            <legend>{{ tc('booking.fanClubFieldsTitle') }}</legend>
            <div class="flex-grid-2-col gutter-md">
              <div class="input">
                <input
                  id="agentTitle"
                  type="text"
                  class="req"
                  name="agentTitle"
                  maxlength="255"
                  data-val="true"
                  :data-val-required="getValidationForField('required', 'title')"
                  data-field-type="SingleLineText"
                  :data-default-value="agent && agent.agentName && agent.agentName.salutation"
                >
                <label
                  for="agentTitle"
                  class="req floating"
                >
                  {{ tc('formFields.title') }}
                </label>
                <span
                  data-valmsg-for="agentTitle"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input">
                <input
                  id="agentFirstname"
                  type="text"
                  class="req"
                  name="agentFirstname"
                  maxlength="255"
                  data-val="true"
                  data-rule-minlength="1"
                  data-rule-maxlength="50"
                  :data-msg-minlength="getValidationForField('minChars', '1')"
                  :data-msg-maxlength="getValidationForField('maxChars', '50')"
                  :data-val-no-special-chars="tc('validations.noSpecialChars')"
                  :data-val-required="getValidationForField('required', 'firstName')"
                  data-field-type="SingleLineText"
                  :data-default-value="agent && agent.agentName && agent.agentName.firstName"
                >
                <label
                  for="agentFirstname"
                  class="req floating"
                >
                  {{ tc('formFields.firstName') }}
                </label>
                <span
                  data-valmsg-for="agentFirstname"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input">
                <input
                  id="agentLastname"
                  type="text"
                  class="req"
                  name="agentLastname"
                  maxlength="255"
                  data-val="true"
                  data-rule-minlength="1"
                  data-rule-maxlength="65"
                  :data-msg-minlength="getValidationForField('minChars', '1')"
                  :data-msg-maxlength="getValidationForField('maxChars', '65')"
                  :data-val-no-special-chars="tc('validations.noSpecialChars')"
                  :data-val-required="getValidationForField('required', 'lastName')"
                  data-field-type="SingleLineText"
                  :data-default-value="agent && agent.agentName && agent.agentName.lastName"
                >
                <label
                  for="agentLastname"
                  class="req floating"
                >
                  {{ tc('formFields.lastName') }}
                </label>
                <span
                  data-valmsg-for="agentLastname"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input">
                <input
                  id="agencyName"
                  type="text"
                  class="req"
                  name="agencyName"
                  maxlength="255"
                  data-val="true"
                  data-rule-minlength="1"
                  data-rule-maxlength="255"
                  :data-msg-minlength="getValidationForField('minChars', '1')"
                  :data-msg-maxlength="getValidationForField('maxChars', '255')"
                  :data-val-required="getValidationForField('required', 'agencyName')"
                  data-field-type="SingleLineText"
                  :data-default-value="agent && agent.agencyAgencyName"
                >
                <label
                  for="agencyName"
                  class="req floating"
                >
                  {{ tc('formFields.agencyName') }}
                </label>
                <span
                  data-valmsg-for="agencyName"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input">
                <input
                  id="agencyId"
                  type="text"
                  class="req"
                  name="agencyId"
                  maxlength="255"
                  data-val="true"
                  data-rule-minlength="1"
                  data-rule-maxlength="255"
                  :data-msg-minlength="getValidationForField('minChars', '1')"
                  :data-msg-maxlength="getValidationForField('maxChars', '255')"
                  :data-val-required="getValidationForField('required', 'agencyId')"
                  data-field-type="SingleLineText"
                  :data-default-value="advisorIata"
                >
                <label
                  for="agencyId"
                  class="req floating"
                >
                  {{ tc('formFields.agencyId') }}
                </label>
                <span
                  data-valmsg-for="agencyId"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input">
                <input
                  id="agencyEmail"
                  type="text"
                  class="req"
                  name="agencyEmail"
                  maxlength="255"
                  data-val="true"
                  data-val-external="email"
                  data-rule-minlength="6"
                  data-rule-maxlength="255"
                  :data-msg-minlength="getValidationForField('minChars', '6')"
                  :data-msg-maxlength="getValidationForField('maxChars', '255')"
                  :data-external-msg="getValidationForField('externalEmail')"
                  :data-val-required="getValidationForField('required', 'email')"
                  :data-val-regex="getValidationForField('email')"
                  data-val-regex-pattern="^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,17}$"
                  data-field-type="Email"
                  :data-default-value="agent && agent.agentEmail"
                >
                <label
                  for="agencyEmail"
                  class="req floating"
                >
                  {{ tc('formFields.email') }}
                </label>
                <span
                  data-valmsg-for="agencyEmail"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
            </div>
          </fieldset>
          <hr class="spacer">
          <fieldset>
            <legend>{{ tc('booking.fanClubClientInfo') }}</legend>
            <div class="flex-grid-1-col gutter-md">
              <div class="fomo-id">
                <div class="input">
                  <input
                    id="email"
                    ref="emailInput"
                    type="text"
                    class="req"
                    :data-default-value="user && user.email"
                    name="email"
                    maxlength="255"
                    data-val="true"
                    data-val-external="email"
                    data-rule-minlength="6"
                    data-rule-maxlength="255"
                    :data-msg-minlength="getValidationForField('minChars', '6')"
                    :data-msg-maxlength="getValidationForField('maxChars', '255')"
                    :data-val-required="getValidationForField('required', 'email')"
                    :data-val-regex="getValidationForField('email')"
                    data-val-regex-pattern="^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,17}$"
                    data-field-type="Email"
                  >
                  <label
                    for="email"
                    class="req floating"
                  >
                    {{ tc('formFields.email') }}
                  </label>
                  <span
                    data-valmsg-for="email"
                    data-valmsg-replace="true"
                    class="field-validation-valid"
                  />
                </div>
                <div class="input checkbox">
                  <label>
                    <input
                      id="fomoid"
                      v-model="isFanMo"
                      type="checkbox"
                      name="fan-mo"
                      data-val="true"
                      data-field-type="CheckBoxSet"
                    >
                    <span>{{ tc('formFields.clientIsFomo') }}</span>
                  </label>
                  <span
                    data-valmsg-for="fomoid"
                    data-valmsg-replace="true"
                    class="field-validation-valid"
                  />
                </div>
                <div
                  v-if="isFanMo"
                  class="fomo-id__look-up"
                >
                  <div class="input">
                    <input
                      id="fansmoid"
                      ref="fomoIdInput"
                      type="text"
                      class="req"
                      :data-default-value="user && user.fanId"
                      name="fansmoid"
                      maxlength="25"
                      data-val="true"
                      data-rule-maxlength="25"
                      :data-val-required="getValidationForField('required', 'fomoId')"
                      :data-msg-maxlength="getValidationForField('maxChars', '25')"
                      data-field-type="SingleLineText"
                    >
                    <label
                      for="fansmoid"
                      class="req floating"
                    >
                      {{ tc('formFields.fomoId') }}
                    </label>
                    <span
                      data-valmsg-for="fansmoid"
                      data-valmsg-replace="true"
                      class="field-validation-valid"
                    />
                  </div>
                  <button
                    :disabled="loadingFomoData"
                    type="button"
                    class="button button--secondary black size-md outlined"
                    @click="lookUpFomoData"
                  >
                    {{ tc('formFields.lookUp') }}
                  </button>
                </div>
                <span
                  v-if="displayFomoError"
                  class="fomo-id__error"
                >
                  {{ fomoError }}
                </span>
              </div>
            </div>
            <div class="flex-grid-2-col gutter-md">
              <div class="input dropdown user-titles">
                <select
                  id="usertitle"
                  class="req"
                  name="title"
                  :data-default-value="user && user.title"
                  data-val="true"
                  :data-val-required="getValidationForField('required', 'title')"
                  data-field-type="DropdownList"
                >
                  <option value="" />
                  <option
                    v-for="title in userTitles"
                    :key="title.value"
                    :value="title.value"
                  >
                    {{ title.label }}
                  </option>
                </select>
                <label
                  for="usertitle"
                  class="floating req"
                >
                  {{ tc('formFields.title') }}
                </label>
                <span
                  data-valmsg-for="title"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
            </div>
            <div class="flex-grid-2-col gutter-md">
              <div class="input">
                <input
                  id="firstname"
                  type="text"
                  class="req"
                  :data-default-value="user && user.firstName"
                  name="firstname"
                  maxlength="255"
                  data-val="true"
                  data-rule-minlength="1"
                  data-rule-maxlength="50"
                  :data-msg-minlength="getValidationForField('minChars', '1')"
                  :data-msg-maxlength="getValidationForField('maxChars', '50')"
                  :data-val-required="getValidationForField('required', 'firstName')"
                  :data-val-no-special-chars="tc('validations.noSpecialChars')"
                  data-field-type="SingleLineText"
                >
                <label
                  for="firstname"
                  class="req floating"
                >
                  {{ tc('formFields.firstName') }}
                </label>
                <span
                  data-valmsg-for="firstname"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input">
                <input
                  id="lastname"
                  type="text"
                  class="req"
                  :data-default-value="user && user.lastName"
                  name="lastname"
                  maxlength="255"
                  data-val="true"
                  data-rule-minlength="1"
                  data-rule-maxlength="65"
                  :data-msg-minlength="getValidationForField('minChars', '1')"
                  :data-msg-maxlength="getValidationForField('maxChars', '65')"
                  :data-val-required="getValidationForField('required', 'lastName')"
                  :data-val-no-special-chars="tc('validations.noSpecialChars')"
                  data-field-type="SingleLineText"
                >
                <label
                  for="lastname"
                  class="req floating"
                >
                  {{ tc('formFields.lastName') }}
                </label>
                <span
                  data-valmsg-for="lastname"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input dropdown country-code">
                <phone-country-select>
                  <template #default="{ handleSelectChange }">
                    <select
                      id="phoneCountryCode"
                      :data-default-value="getCountryCodeDefaultSelectValue()"
                      name="phoneCountryCode"
                      data-val="true"
                      data-val-external="phone"
                      :data-external-msg="getValidationForField('externalPhone')"
                      :data-val-required="getValidationForField('required', 'phoneCountryCode')"
                      data-field-type="DropdownList"
                      @change="handleSelectChange"
                    >
                      <option value="" />
                      <template v-if="!isBookingEditMode">
                        <option
                          v-for="country in countries"
                          :key="country.Name + 'Phone'"
                          :value="country.CallingCode"
                          :data-code="`+${country.CallingCode}`"
                          :data-country="country.Name"
                        >
                          {{ country.Name }} (+{{ country.CallingCode }})
                        </option>
                      </template>
                      <template v-if="isBookingEditMode">
                        <option
                          v-for="country in countries"
                          :key="country.Name + 'Phone'"
                          :value="country.CallingCode + '-' + country.IsoCode"
                          :data-code="`+${country.CallingCode}`"
                          :data-original-text="`${country.Name} (+${country.CallingCode})`"
                        >
                          {{ country.Name }} (+{{ country.CallingCode }})
                        </option>
                      </template>
                    </select>
                  </template>
                </phone-country-select>
                <label
                  for="phoneCountryCode"
                  class="req floating"
                >
                  {{ tc('formFields.phoneCountryCode') }}
                </label>
                <span
                  data-valmsg-for="phoneCountryCode"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input phone">
                <input
                  id="phoneNumber"
                  type="tel"
                  :data-default-value="userPhoneNumber"
                  name="phoneNumber"
                  data-val="true"
                  data-val-external="phone"
                  :data-external-msg="getValidationForField('externalPhone')"
                  :data-val-required="getValidationForField('required', 'phoneNumber')"
                  data-val-regex-pattern="^[\d\+\-\(\)\s]*$"
                  :data-val-regex="tc('validations.phone')"
                  data-rule-minlength="5"
                  data-rule-maxlength="17"
                  :data-msg-minlength="getValidationForField('minChars', '5')"
                  :data-msg-maxlength="getValidationForField('maxChars', '17')"
                  :data-val-phone-number="tc('validations.phone')"
                  data-field-type="PhoneNumber"
                >
                <label
                  for="phoneNumber"
                  class="req floating"
                >
                  {{ tc('formFields.phoneNumber') }}
                </label>
                <span
                  data-valmsg-for="phoneNumber"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
            </div>
            <div class="flex-grid-1-col gutter-md">
              <div class="input dropdown">
                <select
                  id="country"
                  name="country"
                  :data-default-value="userCountry"
                  class=""
                  data-val="true"
                  :data-val-required="getValidationForField('required', 'country')"
                  data-field-type="DropdownList"
                  @change="selectedCountry = $event.target.value"
                >
                  <option value="">
                    {{ tc('formFields.selectCountry') }}
                  </option>
                  <option
                    v-for="country in countries"
                    :key="country.Name + 'Country'"
                    :value="country.IsoCode"
                  >
                    {{ country.Name }}
                  </option>
                </select>
                <label
                  for="country"
                  class="req floating"
                >
                  {{ tc('formFields.country') }}
                </label>
                <span
                  data-valmsg-for="country"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input">
                <input
                  id="streetAddress"
                  type="text"
                  class="req"
                  :data-default-value="userAddressLineOne"
                  name="streetAddress"
                  maxlength="255"
                  data-val="true"
                  data-rule-minlength="1"
                  data-rule-maxlength="255"
                  :data-msg-minlength="getValidationForField('minChars', '1')"
                  :data-msg-maxlength="getValidationForField('maxChars', '255')"
                  :data-val-required="getValidationForField('required', 'stretAdress')"
                  :data-val-address="tc('validations.address')"
                  data-field-type="SingleLineText"
                >
                <label
                  for="streetAddress"
                  class="req floating"
                >
                  {{ tc('formFields.stretAdress') }}
                </label>
                <span
                  data-valmsg-for="streetAddress"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input">
                <input
                  id="city"
                  type="text"
                  class="req"
                  :data-default-value="userCity"
                  name="city"
                  maxlength="255"
                  data-val="true"
                  :data-val-required="getValidationForField('required', 'city')"
                  data-field-type="SingleLineText"
                  data-rule-minlength="1"
                  data-rule-maxlength="50"
                  :data-msg-minlength="getValidationForField('minChars', '1')"
                  :data-msg-maxlength="getValidationForField('maxChars', '50')"
                >
                <label
                  for="city"
                  class="req floating"
                >
                  {{ tc('formFields.city') }}
                </label>
                <span
                  data-valmsg-for="city"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
            </div>
            <div class="flex-grid-2-col gutter-md">
              <div
                class="input"
                :class="[{ dropdown: haveStates }]"
              >
                <input
                  v-if="!haveStates"
                  id="state"
                  type="text"
                  :data-default-value="userState"
                  name="state"
                  maxlength="255"
                  data-field-type="SingleLineText"
                >
                <select
                  v-else
                  id="state"
                  type="text"
                  class=""
                  :data-default-value="userState"
                  data-val="true"
                  :data-val-required="getValidationForField('required', 'state')"
                  name="state"
                  maxlength="255"
                  data-field-type="DropdownList"
                >
                  <option
                    value=""
                    v-text="'Select state'"
                  />
                  <option
                    v-for="state in availableStates"
                    :key="state.value || state.label"
                    :value="state.value || state.label"
                    v-text="state.label"
                  />
                </select>
                <label
                  for="state"
                  class="floating"
                  :class="[{ req: haveStates }]"
                >
                  {{ tc('formFields.state') }}
                </label>
                <span
                  v-if="haveStates"
                  data-valmsg-for="state"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div class="input">
                <input
                  id="zipCode"
                  type="text"
                  class="req"
                  :data-default-value="userPostalCode"
                  name="zipCode"
                  maxlength="255"
                  data-val="true"
                  data-rule-minlength="1"
                  data-rule-maxlength="26"
                  :data-msg-minlength="getValidationForField('minChars', '1')"
                  :data-msg-maxlength="getValidationForField('maxChars', '26')"
                  :data-val-required="getValidationForField('required', 'zipCode')"
                  :data-val-zipcode="tc('validations.zipcode')"
                  data-field-type="SingleLineText"
                >
                <label
                  for="zipCode"
                  class="req floating"
                >
                  {{ tc('formFields.zipCode') }}
                </label>
                <span
                  data-valmsg-for="zipCode"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div
                v-if="!isBookingEditMode"
                class="input"
              >
                <input
                  id="dateOfBirth"
                  type="date"
                  class=""
                  :data-default-value="dateFormatted"
                  name="dateOfBirth"
                  maxlength="255"
                  data-val="true"
                  data-field-type="SingleLineText"
                >
                <label
                  for="dateOfBirth"
                  class="floating"
                >
                  {{ tc('formFields.dateOfBirth') }}
                </label>
                <span
                  data-valmsg-for="dateOfBirth"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
            </div>
            <div class="flex-grid-1-col gutter-md">
              <div
                v-if="isBookingEditMode"
                class="additional-guests-wrapper"
              >
                <div
                  v-if="ModifyReservationAdditionalGuestTitle"
                  class="guest-title"
                >
                  {{ ModifyReservationAdditionalGuestTitle }}
                </div>
                <div
                  v-if="ModifyReservationAdditionalGuestSubTitle"
                  class="guest-subtitle"
                >
                  {{ ModifyReservationAdditionalGuestSubTitle }}
                </div>
                <div class="additional-guests">
                  <div
                    v-for="(reservation, confirmationNumber, index) in groupedReservations"
                    :key="confirmationNumber"
                    class="additional-guest"
                  >
                    <div
                      v-if="isMultiRoomReservation"
                      class="additional-guest-room-name-number"
                    >
                      {{ tc('noums.roomSingular') }} {{ getRoomNumberOrder(confirmationNumber) }}
                    </div>
                    <div
                      v-if="isMultiRoomReservation"
                      class="additional-guest-room-name-label"
                    >
                      {{ getRoomName(confirmationNumber) }}
                    </div>
                    <div
                      v-for="(guest, i) in additionalGuests"
                      :key="'guest-' + guest.id"
                      class="additional-guest-input"
                      :class="{ 'has-guests': additionalGuests.length }"
                    >
                      <additional-guests-input
                        v-if="guest.confirmationNumber === confirmationNumber"
                        ref="additionalGuestInput"
                        v-model="additionalGuests[guest.id]"
                        :index="i + 1"
                        :guest="guest"
                        @remove="removeGuest(guest.id)"
                      />
                    </div>
                    <button
                      class="add-guest"
                      :class="{ 'hide': guestCounts[confirmationNumber] >= 2 }"
                      @click.prevent="addGuest(confirmationNumber)"
                    >
                      <span class="icon-plus" />
                      <span
                        v-if="ModifyReservationAdditionalGuestCTALabel"
                        class="label"
                      >{{ ModifyReservationAdditionalGuestCTALabel }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
          <hr class="spacer">
          <fieldset v-if="!isDirectBill">
            <legend>{{ tc('booking.fcpaymentInformation') }}</legend>
            <CardManagement
              v-if="isBookingEditMode"
              :vgs-endpoint="vgsBaseUrl"
              :is-using-vgs="isUsingVgs"
            />
            <vgs-form
              v-else-if="isUsingVgs"
              ref="vgsForm"
              variant="default"
              :card-holder="tc('formFields.cardHolder')"
              :card-number-field="tc('formFields.cardNumber')"
              :exp-date-field="tc('formFields.cardExpirationDate')"
              :cvc-field="tc('formFields.cardCvc')"
              :vgs-endpoint="vgsBaseUrl"
              field-class="input"
            />
            <credit-card-form v-else />
          </fieldset>
          <hr
            v-if="!isDirectBill"
            class="spacer"
          >
          <fieldset>
            <legend>{{ tc('booking.fcadditionalInformation') }}</legend>
            <p class="reasons-for-stay-label req">
              {{ tc('booking.reasonsForStayLabel') }}
            </p>
            <div class="input radio">
              <label
                v-for="reason in reasonsToStay"
                :key="reason.value"
              >
                <input
                  :id="reason.value"
                  type="radio"
                  name="reasonsToStay"
                  :value="reason.value"
                  :data-default-value="reason.value"
                  data-val="true"
                  :data-val-required="getValidationForField('stayReasons')"
                >
                {{ reason.label }}
              </label>
              <span
                data-valmsg-for="reasonsToStay"
                data-valmsg-replace="true"
                class="field-validation-valid"
              />
            </div>
            <p>{{ tc('booking.anyThingAnythingelseYourStay') }}</p>
            <div class="input">
              <textarea
                id="details"
                name="details"
                rows="10"
                :placeholder="tc('booking.enterDetails')"
              />
            </div>
          </fieldset>
          <hr class="spacer">
          <fieldset
            v-if="!isFanClubBooking && showRoomUpgradeButton && !isBookingEditMode"
            class="room-upgrade-banner-fieldset"
          >
            <legend class="sr-only">
              {{ tc('booking.upgradeRoom') }}
            </legend>
            <room-upgrade-banner />
          </fieldset>
          <input
            v-if="roomUpgradeSelected"
            type="hidden"
            :value="upgradedDetails"
            name="UpgradedDetails"
          >
          <input
            v-if="roomUpgradeSelected"
            type="hidden"
            :value="upgradePriceDifferencePerNight"
            name="UpgradePriceDifferencePerNight"
          >
          <input
            type="hidden"
            :value="isDirectBill"
            name="directBill"
          >
          <input
            type="hidden"
            value="123"
            name="directBillAddress"
          >
          <hr
            v-if="!isFanClubBooking && showRoomUpgradeButton && !isBookingEditMode"
            class="spacer"
          >
          <fieldset>
            <legend>{{ tc('booking.fctermsAndContitionsInformation') }}</legend>
            <div class="flex-grid-1-col gutter-md terms-wrapper">
              <div
                v-if="check.isMdAndDown"
                class="booking-terms"
              >
                {{ tc('bookingTerms.termsAndPoliciesText') }}
                <a
                  :href="tc('bookingTerms.termsAndPoliciesLink')"
                  target="_blank"
                >
                  {{ tc('bookingTerms.termsAndPoliciesLabel') }}
                </a>
              </div>
              <div
                v-for="(term, i) in hotelTerms"
                :key="'terms' + i"
                class="input checkbox"
              >
                <label>
                  <input
                    :id="'terms_' + i"
                    type="checkbox"
                    :name="'terms_' + i"
                    data-val="true"
                    :data-val-ischecked="tc('validations.ischecked')"
                    data-field-type="CheckBoxSet"
                  >
                  <span v-html="term" />
                </label>
                <span
                  :data-valmsg-for="'terms_' + i"
                  data-valmsg-replace="true"
                  class="field-validation-valid"
                />
              </div>
              <div
                v-if="check.isLgAndUp"
                class="booking-terms"
              >
                {{ tc('bookingTerms.termsAndPoliciesText') }}
                <a
                  :href="tc('bookingTerms.termsAndPoliciesLink')"
                  target="_blank"
                >
                  {{ tc('bookingTerms.termsAndPoliciesLabel') }}
                </a>
              </div>
            </div>
          </fieldset>
          <hr class="spacer no-margin">
          <div class="form-footer">
            <div class="overview-item no-margin">
              <div class="item-label bold-item">
                {{ tc('booking.total') }}
              </div>
              <div
                v-if="cart"
                class="item-value"
              >
                {{ cartTotal }}
                <a
                  v-if="check.isLgAndDown"
                  href="#"
                  class="details"
                  @click.prevent="$emit('show-cart')"
                >{{ tc('booking.details') }}</a>
              </div>
            </div>
            <button
              type="submit"
              name="checkout-form-submit"
              class="button"
              :class="[{ loading: isLoading }]"
              value="Submit Button"
            >
              {{ tc('booking.checkOut') }}
              <div
                class="sr-only"
                role="status"
              >
                {{ isLoading ? tc('accessibility.loadingButtonStatus') : '' }}
              </div>
            </button>
            <div
              v-if="serverErrorMsg"
              class="error-box"
            >
              <span>{{ serverErrorMsg }}</span>
            </div>
          </div>
        </form>
      </checkout-form>
      <div class="restriction-policy">
        <div class="restriction-policy-wrapper">
          <div class="section-title">
            {{ tc('booking.restrictionPolicy') }}
          </div>
          <div
            v-for="(item, index) in ratesRestrictionPolicy"
            :key="index"
            class="restriction-policy-item"
          >
            <div
              class="restriction-policy-title"
              v-html="item.title"
            />
            <ul
              class="restriction-policy-body"
              v-html="item.restrictionPolicy"
            />
          </div>
        </div>
      </div>
      <div class="disclaimer">
        <p>{{ tc('booking.riskFreeBooking') }}</p>
        <p v-if="tc('bookingTerms.freeCancellationsLink')">
          <a
            :href="tc('bookingTerms.freeCancellationsLink')"
            target="_blank"
          >{{
            tc('bookingTerms.freeCancellationsLabel')
          }}</a>
          {{ tc('booking.upTo') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import { formatDate, formatDateZone } from '@utils/date-helpers'
  import CreditCardForm from '@components/05.security/credit-card-form/credit-card-form.vue'
  import PhoneCountrySelect from '@components/01.base/01.forms/select/phone-country-select'
  import RoomUpgradeBanner from '../../../components/room-upgrade-banner/room-upgrade-banner.vue'
  import ModifyEditBanner from '../../../components/modify-edit-banner/modify-edit-banner.vue'
  import AdditionalGuestsInput from './additional-guests-input.vue'
  import CardManagement from './card-management.vue'
  import axios from 'axios'
  import { getApiUrl } from '@utils/api-helpers'

  export default
    {
      name: 'BookingCheckOut',
      components: {
        CreditCardForm,
        RoomUpgradeBanner,
        ModifyEditBanner,
        AdditionalGuestsInput,
        CardManagement,
        PhoneCountrySelect,
      },
      inject: ['vgsBaseUrl'],
      props: {
        reasonsToStay: {
          type: Array
        },
        userTitles: {
          type: Array,
        },
        orderConfirmationPage: {
          type: String
        },
        modifyDetailPage: {
          type: String
        }
      },
      data() {
        return {
          isLoading: false,
          selectedCountry: null,
          requiredFields: ['title', 'firstName', 'lastName', 'email', 'phoneNumbers', 'addresses', 'birthDate'],
          additionalGuests: [],
          hideAdditionGuestButton: false,
          selectedPhoneCountryCode: null,
          isFanMo: false,
          fomoData: null,
          loadingFomoData: false,
          displayFomoError: false,
          fomoError: ''
        }
      },
      computed: {
        ...mapGetters('auth', ['isAuthenticated']),
        ...mapGetters('i18n', ['tc']),
        ...mapState('agentAuth', { authAgent: 'user' }),
        ...mapState('booking', {
          bookingReady: 'ready',
          query: 'query',
          checkOutPending: 'checkOutPending'
        }),
        ...mapState('cart', ['cart', 'showRoomUpgradeButton', 'roomUpgradeLoading', 'roomUpgradeSelected']),
        ...mapState('global', ['countries', 'isFanClubBooking']),
        ...mapState('responsive', ['check']),
        ...mapState('modifyReservation', ['currentReservationData', 'isBookingEditMode', 'selectedReservations']),
        ...mapGetters('currency', ['getDisplayPrice', 'currencyData']),
        ...mapGetters('booking', ['selectedHotel', 'getBookingTrackingV2', 'getQuery']),
        ...mapGetters('booking', ['selectedHotel']),
        ...mapGetters('cart', ['itemsInCart']),
        ...mapGetters('modifyReservation', [
          'getBookingTrackingModifyReservation',
          'reservationGuestProfile',
          'storedModifyBookingPropData',
          'reservationGuestProfile',
          'isMultiRoomReservation'
        ]),
        user() {
          if (this.isBookingEditMode) {
            return this.reservationGuestProfile
          }

          return this.fomoData
        },
        agent() {
          return this.authAgent
        },
        isDirectBill() {
          const directBillItems = this.cart.lines.filter((item) => item.guaranteeCode === 'MO Direct Bill')

          return directBillItems.length === this.cart.lines.length
        },
        isUsingVgs() {
          return this.vgsBaseUrl !== ''
        },
        checkoutFormApi() {
          return this.isBookingEditMode ? '/api/v1/booking/ModifyReservation' : '/api/v1/booking/check-out'
        },
        checkoutRedirectUrl() {
          return this.isBookingEditMode ? this.modifyDetailPage : this.orderConfirmationPage
        },
        availableStates() {
          return this.selectedCountry ? this.countries.find((c) => c.IsoCode === this.selectedCountry)?.States : null
        },
        haveStates() {
          return this.availableStates && this.availableStates.length > 0
        },
        hotelTerms() {
          return this.selectedHotel.bookingTerms ? this.selectedHotel.bookingTerms : [`${this.tc('bookingTerms.termsAndConditionsText')}`]
        },
        cartTotal() {
          return this.getDisplayPrice({ price: this.cart.totals.total, withIsoCode: true, isoAfter: true })
        },
        ratesRestrictionPolicy() {
          let restrictionPolicies = this.cart.lines.map((item) => ({ title: this.cart.lines.length > 1 ? item.sitecoreContent.rate.title : '', restrictionPolicy: item.restrictionPolicy }))
          restrictionPolicies = restrictionPolicies.filter((value, index, items) => index === items.findIndex((item) => item.restrictionPolicy === value.restrictionPolicy && item.title === value.title))
          return restrictionPolicies
        },
        dateFormatted: {
          get() {
            return this.user && this.user.birthDate ? formatDateZone(this.user.birthDate, null, 'YYYY-MM-DD') : null
          },
          set(newValue) {
            this.user.birthDate = newValue
          }
        },
        countryCodeFormatted: {
          get() {
            if (this.user && this.user.phoneNumbers && this.user.phoneNumbers.length) {
              if (this.user.phoneNumbers[0].countryCode?.startsWith('+')) {
                return this.user.phoneNumbers[0].countryCode.substring(1)
              }
              return this.user.phoneNumbers[0].countryCode
            }
            return ''
          },
          set(newValue) {
            this.user.phoneNumbers[0].countryCode = newValue
          }
        },
        countryCodeWithUserCountry: {
          get() {
            if (this.user && this.user.phoneNumbers && this.user.phoneNumbers.length) {
              if (this.user.phoneNumbers[0].countryCode?.startsWith('+')) {
                return `${this.user.phoneNumbers[0].countryCode.substring(1)}-${this.user.phoneNumbers[0].countryIsoCode}`
              }
              return `${this.user.phoneNumbers[0].countryCode}-${this.user.phoneNumbers[0].countryIsoCode}`
            }
            return ''
          },
          set(newValue) {
            this.user.phoneNumbers[0].countryCode = newValue
          }
        },
        userPhoneNumber: {
          get() {
            return this.user && this.user.phoneNumbers && this.user.phoneNumbers.length
              ? this.user.phoneNumbers[0].number
              : ''
          },
          set(newValue) {
            this.user.phoneNumbers[0].number = newValue
          }
        },
        userCountry: {
          get() {
            return this.hasAddress ? this.user.addresses[0].country : ''
          },
          set(newValue) {
            this.user.addresses[0].country = newValue
          }
        },
        userState: {
          get() {
            return this.hasAddress ? this.user.addresses[0].state : ''
          },
          set(newValue) {
            this.user.addresses[0].state = newValue
          }
        },
        userCity: {
          get() {
            return this.hasAddress ? this.user.addresses[0].city : ''
          },
          set(newValue) {
            this.user.addresses[0].city = newValue
          }
        },
        userPostalCode: {
          get() {
            return this.hasAddress ? this.user.addresses[0].postalCode : ''
          },
          set(newValue) {
            this.user.addresses[0].postalCode = newValue
          }
        },
        userAddressLineOne: {
          get() {
            return this.hasAddress ? this.user.addresses[0].addressLineOne : ''
          },
          set(newValue) {
            this.user.addresses[0].addressLineOne = newValue
          }
        },
        advisorIata: {
          get() {
            if (this.getQuery.iataNumber) {
              return this.getQuery.iataNumber
            }
            if (this.agent) {
              if (this.agent.agentActiveIataCode && this.agent.agentActiveIataCode != '') {
                return this.agent.agentActiveIataCode
              }
              if (this.agent.agentAgencyIataCodes && this.agent.agentAgencyIataCodes.length) {
                return this.agent.agentAgencyIataCodes[0]
              }
            }
            return ''
          },
          set(newValue) {
            this.getQuery.iataNumber = newValue
          }
        },
        hasAddress() {
          return this.user && this.user.addresses && this.user.addresses.length > 0
        },
        upgradedDetails() {
          const oldBedTypeCode = localStorage.getItem('oldBedTypeCode')
          return oldBedTypeCode && this.itemsInCart[0].bedTypeCode ? `Room ID - Old ${oldBedTypeCode} New ${this.itemsInCart[0].bedTypeCode}` : ''
        },
        upgradePriceDifferencePerNight() {
          return this.itemsInCart[0].upgradePriceDifferencePerNight
        },
        guestCounts() {
          const counts = {}
          Object.entries(this.groupedReservations).forEach(([confirmationNumber, reservation]) => {
            counts[confirmationNumber] = this.additionalGuests.reduce((count, guest) => (guest.confirmationNumber === confirmationNumber ? count + 1 : count), 0)
          })
          return counts
        },
        maxGuestsAllowed() {
          return 2
        },
        groupedReservations() {
          const groups = {}
          this.selectedReservations.forEach((reservation) => {
            if (!groups[reservation.id]) {
              groups[reservation.id] = []
            }
            groups[reservation.id].push(
              reservation
            )
          })
          return groups
        },
        ModifyReservationAdditionalGuestTitle() {
          return this.storedModifyBookingPropData?.ModifyReservationAdditionalGuestTitle
            ? this.storedModifyBookingPropData.ModifyReservationAdditionalGuestTitle : ''
        },
        ModifyReservationAdditionalGuestSubTitle() {
          return this.storedModifyBookingPropData?.ModifyReservationAdditionalGuestSubTitle
            ? this.storedModifyBookingPropData.ModifyReservationAdditionalGuestSubTitle : ''
        },
        ModifyReservationAdditionalGuestCTALabel() {
          return this.storedModifyBookingPropData?.ModifyReservationAdditionalGuestCTALabel
            ? this.storedModifyBookingPropData.ModifyReservationAdditionalGuestCTALabel : ''
        },
      },
      watch: {
        user(val) {
          if (val) {
            if (this.userCountry) {
              this.selectedCountry = this.userCountry
            }
            this.$nextTick(() => {
              this.$refs.checkoutForm?.updateDefaultValues()
              if (this.user.phoneNumbers.length) {
                this.$refs.checkoutForm?.onExternalValidation({ data: { validationType: 'phone' } })
              }
              if (this.user.email) {
                this.$refs.checkoutForm?.onExternalValidation({ data: { validationType: 'email' } })
              }
            })
          }
        },
        availableStates(val) {
          if (this.$refs.checkoutForm) {
            this.$nextTick(() => {
              this.$refs.checkoutForm.reInitForm()
            })
          }
        },
        additionalGuests(val) {
          if (this.$refs.checkoutForm) {
            this.$nextTick(() => {
              this.$refs.checkoutForm.reInitForm()
            })
          }
        },
      },
      mounted() {
        if (this.user) {
          if (this.userCountry) {
            this.selectedCountry = this.userCountry
          }
        }
        this.$track(this.getBookingTrackingV2(this.getQuery, { event: 'checkout' }))

        if (this.isBookingEditMode) {
          if (this.$route.query.bookingEditMode === 'true') {
            this.$nextTick(() => {
              this.$track(this.getBookingTrackingModifyReservation({
                name: 'home >> modify >> find-reservation >> details >> modify-guest-details',
                hotelCode: this.$route.query.hotelCode,
                siteSection: 'find - details - modify-guest-details'
              }, {}))
            })
          }
          this.additionalGuests = this.selectedReservations.flatMap((reservation) => {
            // Check if there are guests in the reservation
            if (reservation.guests && reservation.guests.length > 1) {
              return reservation.guests
                .filter((guest) => guest.isPrimary === false) // Filter for guests where isPrimary is false
                .map((guest) => {
                  const firstName = guest.profiles.profileInfo.profile.customer.personName.givenName[0]
                  const lastName = guest.profiles.profileInfo.profile.customer.personName.surname
                  const { roomName } = guest
                  return {
                    firstName,
                    lastName,
                    confirmationNumber: reservation.id,
                    roomName,
                    id: this.generateNineDigitGuestId()
                  }
                })
            }
            return []
          })
        }
      },
      methods: {
        formatDate,
        formatDateZone,
        getValidationForField(validation, field = '') {
          return this.tc(`validations.${validation}`).replace('{0}', this.tc(`formFields.${field}`))
        },
        onExternalError(e) {
          this.isLoading = false
          this.editMode = true
        },
        addGuest(confirmationNumber) {
          const newGuest = {
            firstName: '',
            lastName: '',
            id: this.generateNineDigitGuestId(),
            confirmationNumber
          }
          this.additionalGuests.push(newGuest)
        },
        removeGuest(id) {
          this.additionalGuests = this.additionalGuests.filter((guest) => guest.id !== id)
          if (this.$refs.checkoutForm) {
            this.$nextTick(() => {
              this.$refs.checkoutForm.reInitForm()
            })
          }
        },
        generateNineDigitGuestId() {
          const min = 100000000
          const max = 999999999
          return Math.floor(Math.random() * (max - min + 1)) + min
        },
        getRoomName(confirmation) {
          return this.cart.lines
            .filter((item) => item.confirmantionNumber === confirmation)
            .map((item) => `${item.sitecoreContent.room.title} | ${item.sitecoreContent.bed.title}`)
            .join('')
        },
        getRoomNumberOrder(confirmation) {
          return this.selectedReservations
            .filter((reservation) => reservation.id === confirmation)
            .map((item) => item.roomNumberOrder).join('')
        },
        getCountryCodeDefaultSelectValue() {
          return !this.isBookingEditMode ? this.countryCodeFormatted : this.countryCodeWithUserCountry
        },
        async lookUpFomoData() {
          this.displayFomoError = false

          if (!this.$refs.emailInput.value) {
            this.$refs.checkoutForm?.forceValidationError(
              this.$refs.emailInput,
              this.$refs.emailInput.dataset.valRequired
            )
            this.$refs.emailInput.focus()
            return
          }

          if (!this.$refs.fomoIdInput.value) {
            this.$refs.checkoutForm?.forceValidationError(
              this.$refs.fomoIdInput,
              this.$refs.fomoIdInput.dataset.valRequired
            )
            this.$refs.fomoIdInput.focus()
            return
          }

          this.loadingFomoData = true

          try {
            const response = await axios.post(getApiUrl('/api/v1/profile/fan-lookup'), {
              fomoId: this.$refs.fomoIdInput.value,
              email: this.$refs.emailInput.value,
            })

            if (response.data.profile) {
              this.fomoData = response.data.profile
            } else {
              this.displayFomoError = true

              if (response?.data?.errors?.UserNotFound) {
                this.fomoError = this.tc('errors.fomoIdNotFound')
              } else if (response?.data?.errors?.EmailNotMatching) {
                this.fomoError = this.tc('errors.fomoIdNotMatching')
              } else {
                this.fomoError = 'There was an error while looking up your Fan Club ID. Please try again.'
              }
              throw new Error(fomoError)
            }
          } catch (error) {
            this.$logger.error(error)
            throw error
          } finally {
            this.loadingFomoData = false
          }
        },
      }
    }
</script>
